/**
 * Default fallback currency used when a tenant has not yet configured one
 * or when no business_settings row is in scope (e.g. anonymous browsing).
 *
 * Keep this in sync with the DEFAULT clause on
 * `public.business_settings.currency`.
 */
export const DEFAULT_CURRENCY = 'SAR';

/**
 * Shape of a currency option returned by /api/currencies.
 * Keep in sync with `apps/web/app/api/currencies/route.ts`.
 */
export interface CurrencyOption {
  /** ISO 4217 alpha-3 code, e.g. 'SAR' (the value to persist). */
  value: string;
  /** Localised display name including the code, e.g. 'Saudi Riyal (SAR)'. */
  label: string;
  /** The currency's localised symbol when one exists, otherwise the code. */
  symbol: string;
}

/**
 * Resolve the currency to use for a given call site.
 *
 * Priority: explicit > tenant > DEFAULT_CURRENCY.
 * Empty strings, whitespace, and nullish values are ignored.
 */
export function resolveCurrency(
  explicit?: string | null,
  tenant?: string | null,
): string {
  return normalize(explicit) ?? normalize(tenant) ?? DEFAULT_CURRENCY;
}

function normalize(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(trimmed) ? trimmed : null;
}

/**
 * Default ISO 3166-1 alpha-2 country for the phone-number input, derived from
 * the tenant's currency. A currency isn't a country (the euro spans 20), but
 * for our single-country Gulf/Levant tenants the mapping is unambiguous and
 * lets the phone input pre-select the right calling code without a separate
 * "country" setting.
 *
 * Only currencies we actively operate in are listed. Anything else falls back
 * to 'SA' (Saudi Arabia), which preserves the historical hardcoded default.
 * Add an entry here when onboarding a tenant in a new country.
 */
const CURRENCY_DEFAULT_COUNTRY: Readonly<Record<string, string>> = {
  SAR: 'SA', // Saudi Arabia
  AED: 'AE', // United Arab Emirates
  JOD: 'JO', // Jordan
  GBP: 'GB', // United Kingdom
};

/**
 * Currency a tenant should default to when it picks this time zone. Only
 * single-currency zones we onboard into are listed; anything else keeps the
 * tenant's current currency.
 */
const TIME_ZONE_DEFAULT_CURRENCY: Readonly<Record<string, string>> = {
  'Asia/Riyadh': 'SAR',
  'Asia/Dubai': 'AED',
  'Asia/Amman': 'JOD',
  'Europe/London': 'GBP',
};

export function timeZoneToDefaultCurrency(
  timeZone: string | null | undefined,
): string | undefined {
  return timeZone ? TIME_ZONE_DEFAULT_CURRENCY[timeZone] : undefined;
}

/** Fallback country when a currency has no explicit mapping. */
export const DEFAULT_PHONE_COUNTRY = 'SA';

/**
 * Resolve the default phone-input country for a currency code.
 *
 *   currencyToDefaultCountry('AED') // 'AE'
 *   currencyToDefaultCountry('JOD') // 'JO'
 *   currencyToDefaultCountry('USD') // 'SA' (unmapped → fallback)
 *
 * Returns a libphonenumber-compatible alpha-2 country code suitable for the
 * `defaultCountry` prop of `<PhoneInput />`.
 */
export function currencyToDefaultCountry(
  code: string | null | undefined,
): string {
  const normalized = normalize(code);
  if (!normalized) return DEFAULT_PHONE_COUNTRY;
  return CURRENCY_DEFAULT_COUNTRY[normalized] ?? DEFAULT_PHONE_COUNTRY;
}

/**
 * Placeholder billing address, keyed by ISO 3166-1 alpha-2 country. Some card
 * gateways (HyperPay/OPPWA) treat billing.country/city/state/postcode as
 * MANDATORY on live checkouts and run fraud/AVS checks against them, so a
 * geographically-consistent placeholder (matching the tenant's country) is
 * safer than a fixed Saudi default when the real address wasn't collected.
 *
 * Values are a real capital + a valid postcode for that country so they pass
 * gateway format validation. Country falls back to the tenant-currency default;
 * an unmapped country falls back to the Saudi placeholder (historical default).
 */
interface DefaultBillingAddress {
  city: string;
  state: string;
  postcode: string;
}

const COUNTRY_DEFAULT_BILLING: Readonly<Record<string, DefaultBillingAddress>> =
  {
    SA: { city: 'Riyadh', state: 'Riyadh', postcode: '11111' },
    AE: { city: 'Dubai', state: 'Dubai', postcode: '00000' },
    JO: { city: 'Amman', state: 'Amman', postcode: '11118' },
  };

const FALLBACK_BILLING: DefaultBillingAddress = {
  city: 'Riyadh',
  state: 'Riyadh',
  postcode: '11111',
};

/**
 * Resolve a full placeholder billing address from a currency code, for
 * gateways that require billing fields but where the real customer address
 * wasn't captured. Country is the currency's default country (alpha-2), and
 * city/state/postcode match that country.
 *
 *   currencyToDefaultBilling('JOD')
 *   // { country: 'JO', city: 'Amman', state: 'Amman', postcode: '11118' }
 *   currencyToDefaultBilling('SAR')
 *   // { country: 'SA', city: 'Riyadh', state: 'Riyadh', postcode: '11111' }
 */
export function currencyToDefaultBilling(code: string | null | undefined): {
  country: string;
  city: string;
  state: string;
  postcode: string;
} {
  const country = currencyToDefaultCountry(code);
  const address = COUNTRY_DEFAULT_BILLING[country] ?? FALLBACK_BILLING;
  return { country, ...address };
}

/**
 * Card networks accepted by the Apple Pay sheet, keyed by ISO 3166-1 alpha-2
 * country. These are Apple Pay JS `supportedNetworks` values (camelCase), NOT
 * the uppercase HyperPay/OPPWA `brands` values.
 *
 * mada is Saudi-only, so it appears solely under SA — requesting it for a
 * Jordanian or Emirati merchant makes the Apple Pay sheet open and immediately
 * dismiss, because Apple validates the requested networks against what the
 * merchant account actually supports.
 */
const COUNTRY_APPLE_PAY_NETWORKS: Readonly<Record<string, readonly string[]>> = {
  SA: ['mada', 'visa', 'masterCard', 'amex'],
  AE: ['visa', 'masterCard', 'amex'],
  JO: ['visa', 'masterCard'],
};

const FALLBACK_APPLE_PAY_NETWORKS: readonly string[] = [
  'mada',
  'visa',
  'masterCard',
  'amex',
];

/**
 * Resolve the Apple Pay sheet configuration for a currency code.
 *
 * `supportedCountries` restricts the merchant's country; `supportedNetworks`
 * the accepted card schemes. Both must match the merchant account, otherwise
 * Apple presents the sheet and dismisses it as soon as the payment request is
 * found unsatisfiable.
 *
 *   currencyToApplePayConfig('JOD')
 *   // { supportedCountries: ['JO'], supportedNetworks: ['visa', 'masterCard'] }
 *   currencyToApplePayConfig('SAR')
 *   // { supportedCountries: ['SA'], supportedNetworks: ['mada', 'visa', 'masterCard', 'amex'] }
 */
export function currencyToApplePayConfig(code: string | null | undefined): {
  supportedCountries: string[];
  supportedNetworks: string[];
} {
  const country = currencyToDefaultCountry(code);
  const networks =
    COUNTRY_APPLE_PAY_NETWORKS[country] ?? FALLBACK_APPLE_PAY_NETWORKS;
  return {
    supportedCountries: [country],
    supportedNetworks: [...networks],
  };
}

/**
 * HyperPay/OPPWA `brands` for a currency — the server-side list sent at
 * prepareCheckout, which constrains what the widget can offer. Uppercase
 * OPPWA brand codes, deliberately kept separate from the Apple Pay JS
 * `supportedNetworks` values above (MASTER vs masterCard, etc.).
 */
const COUNTRY_HYPERPAY_BRANDS: Readonly<Record<string, readonly string[]>> = {
  SA: ['VISA', 'MASTER', 'AMEX', 'MADA'],
  AE: ['VISA', 'MASTER', 'AMEX'],
  JO: ['VISA', 'MASTER'],
};

const FALLBACK_HYPERPAY_BRANDS: readonly string[] = [
  'VISA',
  'MASTER',
  'AMEX',
  'MADA',
];

/**
 * Resolve the HyperPay `brands` list for a currency code.
 *
 *   currencyToHyperpayBrands('JOD') // ['VISA', 'MASTER']
 *   currencyToHyperpayBrands('SAR') // ['VISA', 'MASTER', 'AMEX', 'MADA']
 */
export function currencyToHyperpayBrands(
  code: string | null | undefined,
): string[] {
  const country = currencyToDefaultCountry(code);
  return [...(COUNTRY_HYPERPAY_BRANDS[country] ?? FALLBACK_HYPERPAY_BRANDS)];
}

/**
 * Localised currency symbol (e.g. SAR → 'SAR' in en, 'ر.س.‏' in ar).
 * Falls back to the ISO code when the runtime can't produce a symbol.
 *
 * Internally uses Intl.NumberFormat with `currencyDisplay: 'symbol'` and
 * extracts the symbol parts. This keeps localisation consistent with how
 * `formatCurrency` renders amounts.
 */
export function currencySymbol(
  code: string,
  locale: string = 'en',
): string {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'symbol',
    }).formatToParts(0);
    const symbol = parts
      .filter((p) => p.type === 'currency')
      .map((p) => p.value)
      .join('');
    return symbol || code;
  } catch {
    return code;
  }
}

/**
 * ISO 4217 currencies whose minor-unit exponent ("CcyMnrUnts") differs
 * from the 2-decimal default. Source of truth: SIX Group list-one.xml,
 * the official Maintenance Agency for ISO 4217.
 *
 *   https://www.six-group.com/dam/download/financial-information/data-center/iso-currrency/lists/list-one.xml
 *
 * - Exponent 0 → no fractional subunit (e.g. JPY, KRW). Multiplier × 1.
 * - Exponent 3 → subunit is 1/1000 (e.g. JOD = 1000 fils). Multiplier × 1000.
 * - Exponent 4 → only CLF and UYW (specialised units). Multiplier × 10000.
 *
 * Anything not listed defaults to exponent 2 (the overwhelming majority:
 * SAR, USD, EUR, GBP, AED, …). When SIX amends the list, append/remove
 * entries here — that's the only required change.
 */
const ISO_4217_NON_DEFAULT_MINOR_UNITS: Readonly<Record<string, number>> = {
  // Exponent 0
  BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0,
  PYG: 0, RWF: 0, UGX: 0, UYI: 0, VND: 0, VUV: 0,
  XAF: 0, XOF: 0, XPF: 0,
  // Exponent 3 (Gulf / North-African dinars + a few others)
  BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,
  // Exponent 4
  CLF: 4, UYW: 4,
};

const ISO_4217_DEFAULT_MINOR_UNITS = 2;

/**
 * ISO 4217 minor-unit exponent for a currency code (the number of decimal
 * places in the major→minor relationship). Returns 2 by default.
 */
export function currencyMinorUnits(code: string): number {
  const normalized = code?.toUpperCase();
  return (
    ISO_4217_NON_DEFAULT_MINOR_UNITS[normalized] ??
    ISO_4217_DEFAULT_MINOR_UNITS
  );
}

/**
 * Multiplier (10^exponent) to convert a major-unit amount to the
 * currency's minor unit. SAR/USD → 100, JOD/KWD/BHD/OMR → 1000, JPY → 1.
 */
export function currencyMinorUnitFactor(code: string): number {
  return Math.pow(10, currencyMinorUnits(code));
}

/**
 * Convert a major-unit amount to the currency's minor unit (integer).
 *
 *   toMinorUnits(100, 'SAR')  // 10000   (halalas, 2 decimals)
 *   toMinorUnits(100, 'JOD')  // 100000  (fils, 3 decimals)
 *   toMinorUnits(1000, 'JPY') // 1000    (no fractional unit)
 *
 * Use this anywhere you previously wrote `Math.round(amount * 100)` when
 * handing an amount to a payment provider (Moyasar, Apple Pay, Tap, etc.).
 * The hardcoded `* 100` pattern silently undercharges 3-decimal currencies
 * by 10x and overcharges JPY-style currencies by 100x.
 */
export function toMinorUnits(amount: number, code: string): number {
  return Math.round(amount * currencyMinorUnitFactor(code));
}

/**
 * Convert a minor-unit integer (as returned by Moyasar et al.) back to a
 * major-unit amount.
 *
 *   fromMinorUnits(10000, 'SAR')  // 100
 *   fromMinorUnits(100000, 'JOD') // 100
 *   fromMinorUnits(1000, 'JPY')   // 1000
 *
 * Use this anywhere you previously wrote `paymentStatus.amount / 100`.
 */
export function fromMinorUnits(minor: number, code: string): number {
  return minor / currencyMinorUnitFactor(code);
}

/**
 * Format an amount in the given currency for the given locale.
 *
 *   formatCurrency(250, 'SAR', 'en')      // "SAR 250.00"
 *   formatCurrency(250, 'AED', 'en')      // "AED 250.00"
 *   formatCurrency(250, 'SAR', 'ar')      // "‏٢٥٠٫٠٠ ر.س.‏"
 *
 * Falls back to a code-prefixed string when the runtime can't format the
 * currency (e.g. a typo'd code that survived validation).
 */
export function formatCurrency(
  amount: number,
  code: string,
  locale: string = 'en',
  options?: Intl.NumberFormatOptions,
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      ...options,
    }).format(amount);
  } catch {
    return `${code} ${amount.toFixed(2)}`;
  }
}
